import { CONST } from "@ppb/bet-engine";

/**
 * Resolves a handicap or totals bet into the outcome rows the customer sees — "If Tigre win by 3 or
 * more", "If Tigre win by 2", "If Tigre do not win by 2 or more" — each with its own profit or loss.
 *
 * Ported from the Exchange Mobile Site's `AHOutcomesHelper.getAHPnlOutcomes`
 * (`app/common/scripts/helpers/ah-outcomes/ah-outcomes.helper.js`), which itself mirrors BE1, so the
 * two products read identically. The label keys and their `param1`/`param2` interpolation are EMS's.
 *
 * The values come from the bet engine's PnL table for the potential bet (see `getHandicapPnlTable`).
 * This only picks three points out of it; it never computes a profit itself.
 */

/** The bet engine's map of outcome (goal margin, or goal total) to profit, e.g. `{ 2: 1.9, 1: 0, 0: -2 }`. */
export type PnlTable = Record<string, number>;

/**
 * One outcome the customer can read off the bet, with the translation key to render it.
 *
 * The key is returned rather than a resolved string so this stays free of i18n and platform details —
 * the caller interpolates `params` into it.
 */
export type HandicapOutcome = {
  /** The full translation key, ready for `i18n`. */
  labelKey: HandicapOutcomeKey;
  /** `param1` / `param2` for the label, in EMS's own naming so the keys stay drop-in. */
  params: { param1?: string | number; param2?: number };
  pnl: number;
};

/**
 * The namespace these labels live under. `I18N.LABELS.*` is tbd's existing home for interpolated
 * labels (see `I18N.LABELS.EW_TERMS`), and keeping EMS's own suffixes means the strings can be lifted
 * from `bf-ems-static-client`'s translation files unchanged.
 */
const LABEL_PREFIX = "I18N.LABELS.AH." as const;

/** Every translation key this helper can emit — the set that has to exist for the rows to render. */
export type HandicapOutcomeKey = `${typeof LABEL_PREFIX}${HandicapOutcomeLabel}`;

/** The label suffixes EMS uses. */
export type HandicapOutcomeLabel =
  | "IF_RUNNER_WIN_BY_OR_MORE"
  | "IF_RUNNER_DO_NOT_WIN_BY_OR_MORE"
  | "IF_RUNNER_DRAW_OR_WIN"
  | "IF_RUNNER_WIN"
  | "IF_RUNNER_DO_NOT_WIN"
  | "IF_RUNNER_WIN_BY"
  | "IF_MATCH_DRAW"
  | `SPREADS_AND_TOTALS_LESS_THAN_${ScoreWord}`
  | `SPREADS_AND_TOTALS_EXACTLY_${ScoreWord}`
  | `SPREADS_AND_TOTALS_MORE_THAN_${ScoreWord}`
  // A count of zero is always plural, so these two have no singular form.
  | `SPREADS_AND_TOTALS_ZERO_${PluralScoreWord}`
  | `SPREADS_AND_TOTALS_EXACTLY_ZERO_${PluralScoreWord}`;

type PluralScoreWord = "GOALS" | "POINTS";
type ScoreWord = "GOAL" | "POINT" | PluralScoreWord;

export type OutcomeBet = {
  marketType: string;
  selectionId: number;
  handicap: number;
};

export type OutcomeRunners = {
  home: { selectionId: number; name: string };
  away: { selectionId: number; name: string };
};

type LabelSpec = { labelKey: HandicapOutcomeLabel; params: HandicapOutcome["params"] };

/*
 * The market types bet one line at a time. Deliberately duplicated from tbd-shared's
 * `formatters/handicap-line-formatters.ts` because `@ppb/betslip` must not import `@ppb/tbd-shared` -
 * keep the two in step (both tests pin the same table).
 */

/** Handicaps: the two runners quote one line with opposite handicaps (`Man City -0.5` / `Arsenal +0.5`). */
const HANDICAP_MARKET_TYPES: string[] = [CONST.MARKET.TYPE.ASIAN_HANDICAP, CONST.MARKET.TYPE.HANDICAP];

/** Totals: both runners share one line, a count (`Over 2.5` / `Under 2.5`). */
const TOTALS_MARKET_TYPES: string[] = [CONST.MARKET.TYPE.ALT_TOTAL_GOALS, CONST.MARKET.TYPE.COMBINED_TOTAL];

/** The market types resolved into outcome rows. */
const MARKET_TYPES_WITH_OUTCOMES: string[] = [...HANDICAP_MARKET_TYPES, ...TOTALS_MARKET_TYPES];

/** Lines that carry no `+`/`-` sign. Mirrors EMS's `AH_MARKET_TYPES_WITHOUT_SIGNAL`. */
const MARKET_TYPES_WITHOUT_HANDICAP_SIGN: string[] = [CONST.MARKET.TYPE.ALT_TOTAL_GOALS];

export const isHandicapMarket = (marketType?: string): boolean =>
  !!marketType && HANDICAP_MARKET_TYPES.includes(marketType);

export const shouldHideHandicapSign = (marketType?: string): boolean =>
  !!marketType && MARKET_TYPES_WITHOUT_HANDICAP_SIGN.includes(marketType);

/** The market fields that decide whether a market is bet one line at a time. */
export type LineMarket = { marketType?: string; bettingType?: string };

/**
 * Only double-line markets get outcome rows. A single-line Asian Handicap market can share the
 * `ASIAN_HANDICAP` market type, but it is out of scope.
 */
export const hasHandicapOutcomes = ({ marketType, bettingType }: LineMarket): boolean =>
  bettingType === CONST.MARKET.BETTING_TYPE.ASIAN_HANDICAP_DOUBLE_LINE &&
  !!marketType &&
  MARKET_TYPES_WITH_OUTCOMES.includes(marketType);

/**
 * The PnL table the bet engine keeps for a potential bet, as EMS reads it. Not `calc.betExposure`: that
 * returns what the bet risks, so every winning outcome would show a profit of zero. `undefined` until
 * the customer enters a stake, which keeps the rows hidden.
 *
 * The cast covers `@ppb/bet-engine@10.1.0` typing `PotentialBet.pnl` without its `table`; drop it once
 * `fix/home-selection-id-typing` is released.
 */
export const getHandicapPnlTable = (
  potentialBet: { pnl?: unknown } | undefined,
  market: LineMarket,
): PnlTable | undefined => {
  if (!hasHandicapOutcomes(market)) {
    return undefined;
  }

  const { table } = (potentialBet?.pnl ?? {}) as { table?: PnlTable };

  return table && Object.keys(table).length ? table : undefined;
};

/** A margin line counts goals on football-shaped markets and points elsewhere. Mirrors EMS. */
const pluralScoreWord = (marketType: string): PluralScoreWord =>
  marketType === CONST.MARKET.TYPE.ALT_TOTAL_GOALS || marketType === CONST.MARKET.TYPE.ASIAN_HANDICAP
    ? "GOALS"
    : "POINTS";

const scoreWord = (marketType: string, plural: boolean): ScoreWord => {
  const word = pluralScoreWord(marketType);

  return plural ? word : (word.slice(0, -1) as ScoreWord);
};

/**
 * The margin whose profit is the middle outcome — the one where the bet half-wins or pushes.
 *
 * On a handicap market the bet's handicap is recorded against its own runner, so a bet on the home
 * runner needs it flipped to read as a margin.
 */
const getOutcomeThreshold = ({ marketType, selectionId, handicap }: OutcomeBet, home: OutcomeRunners["home"]) => {
  const threshold = Math.round(handicap);

  if (isHandicapMarket(marketType)) {
    return selectionId === home.selectionId && threshold !== 0 ? -threshold : threshold;
  }

  return threshold;
};

/**
 * The three outcome values, best-first: full win, the middle outcome, then the loss.
 *
 * The middle slot is `undefined` on a half line (`±x.5`), which cannot push — those markets have two
 * outcomes, not three, and the row is dropped.
 */
const getOutcomeValues = (bet: OutcomeBet, pnlTable: PnlTable, runners: OutcomeRunners): Array<number | undefined> => {
  const margins = Object.keys(pnlTable).map(Number);
  const best = pnlTable[Math.max(...margins)];
  const worst = pnlTable[Math.min(...margins)];

  // A totals bet on the home runner reads the other way round: its "win" is the low end of the range.
  const isReversedTotals = bet.selectionId === runners.home.selectionId && !isHandicapMarket(bet.marketType);

  const values: Array<number | undefined> = isReversedTotals ? [worst, undefined, best] : [best, undefined, worst];

  values[1] = Math.abs(bet.handicap % 1) === 0.5 ? undefined : pnlTable[getOutcomeThreshold(bet, runners.home)];

  return bet.selectionId === runners.away.selectionId ? values.reverse() : values;
};

/**
 * Labels for a Totals market — the line is a count, so the runner is not named. A missing label drops
 * its row.
 */
const getTotalsLabels = (marketType: string, handicap: number): Array<LabelSpec | undefined> => {
  const absHandicap = Math.abs(handicap);
  const score = Math.round(absHandicap);
  const word = (plural: boolean) => scoreWord(marketType, plural);

  if (absHandicap === 0.5) {
    return [
      { labelKey: `SPREADS_AND_TOTALS_ZERO_${pluralScoreWord(marketType)}`, params: {} },
      { labelKey: `SPREADS_AND_TOTALS_EXACTLY_${word(false)}`, params: {} },
      { labelKey: `SPREADS_AND_TOTALS_MORE_THAN_${word(false)}`, params: {} },
    ];
  }

  // A line below 0.5 (`0`, or the `0 & 0.5` quarter line) has nothing below zero to settle on, so there is
  // no lower outcome. EMS shows an impossible "-1 goals or less" row here, and "1 goals or more".
  if (score === 0) {
    return [
      undefined,
      { labelKey: `SPREADS_AND_TOTALS_EXACTLY_ZERO_${pluralScoreWord(marketType)}`, params: { param1: 0 } },
      { labelKey: `SPREADS_AND_TOTALS_MORE_THAN_${word(false)}`, params: {} },
    ];
  }

  const lower: LabelSpec =
    score - 1 === 0
      ? { labelKey: `SPREADS_AND_TOTALS_EXACTLY_ZERO_${pluralScoreWord(marketType)}`, params: { param1: 0 } }
      : {
          labelKey: `SPREADS_AND_TOTALS_LESS_THAN_${word(score - 1 !== 1)}`,
          params: { param1: score - 1 },
        };

  const middle: LabelSpec =
    score - 1 === 0
      ? { labelKey: `SPREADS_AND_TOTALS_EXACTLY_${word(false)}`, params: { param1: score } }
      : { labelKey: `SPREADS_AND_TOTALS_EXACTLY_${word(true)}`, params: { param1: score } };

  const upper: LabelSpec = {
    labelKey: `SPREADS_AND_TOTALS_MORE_THAN_${word(true)}`,
    params: { param1: absHandicap % 1 === 0.5 ? score : score + 1 },
  };

  return [lower, middle, upper];
};

/** Labels for a Handicap / Asian Handicap market, which name the runners. */
const getHandicapLabels = (handicap: number, nameA: string, nameB: string): LabelSpec[] => {
  const score = Math.round(Math.abs(handicap));

  const best: LabelSpec = (() => {
    if (handicap < -0.5 && handicap % 1 !== -0.5) {
      return { labelKey: "IF_RUNNER_WIN_BY_OR_MORE", params: { param1: nameA, param2: score + 1 } };
    }
    if (handicap < -0.5) {
      return { labelKey: "IF_RUNNER_WIN_BY_OR_MORE", params: { param1: nameA, param2: score } };
    }
    if (handicap >= 1.5) {
      return { labelKey: "IF_RUNNER_DO_NOT_WIN_BY_OR_MORE", params: { param1: nameB, param2: score } };
    }
    if (handicap >= 0.5) {
      return { labelKey: "IF_RUNNER_DRAW_OR_WIN", params: { param1: nameA } };
    }
    return { labelKey: "IF_RUNNER_WIN", params: { param1: nameA } };
  })();

  const middle: LabelSpec = (() => {
    if (handicap < -0.25) {
      return { labelKey: "IF_RUNNER_WIN_BY", params: { param1: nameA, param2: score } };
    }
    if (handicap < 0.5) {
      return { labelKey: "IF_MATCH_DRAW", params: {} };
    }
    return { labelKey: "IF_RUNNER_WIN_BY", params: { param1: nameB, param2: score } };
  })();

  const worst: LabelSpec = (() => {
    if (handicap <= -1.5) {
      return { labelKey: "IF_RUNNER_DO_NOT_WIN_BY_OR_MORE", params: { param1: nameA, param2: score } };
    }
    if (handicap < -0.25) {
      return { labelKey: "IF_RUNNER_DO_NOT_WIN", params: { param1: nameA } };
    }
    if (handicap <= 0.5) {
      return { labelKey: "IF_RUNNER_WIN", params: { param1: nameB } };
    }
    if (handicap % 1 === 0.5) {
      return { labelKey: "IF_RUNNER_WIN_BY_OR_MORE", params: { param1: nameB, param2: score } };
    }
    return { labelKey: "IF_RUNNER_WIN_BY_OR_MORE", params: { param1: nameB, param2: score + 1 } };
  })();

  return [best, middle, worst];
};

const getOutcomeLabels = (bet: OutcomeBet, runners: OutcomeRunners): Array<LabelSpec | undefined> => {
  if (!isHandicapMarket(bet.marketType)) {
    return getTotalsLabels(bet.marketType, bet.handicap);
  }

  const isHome = bet.selectionId === runners.home.selectionId;

  return getHandicapLabels(
    bet.handicap,
    isHome ? runners.home.name : runners.away.name,
    isHome ? runners.away.name : runners.home.name,
  );
};

/**
 * The outcome rows for a handicap or totals bet, best outcome first.
 *
 * Returns two rows on a half line and on a Totals line below 0.5, and three otherwise. EMS does the same
 * except below 0.5: an outcome the bet cannot reach is dropped rather than shown.
 */
export const getHandicapOutcomes = (
  bet: OutcomeBet,
  pnlTable: PnlTable,
  runners: OutcomeRunners,
): HandicapOutcome[] => {
  if (!Object.keys(pnlTable).length) {
    return [];
  }

  const labels = getOutcomeLabels(bet, runners);

  return getOutcomeValues(bet, pnlTable, runners).reduce<HandicapOutcome[]>((rows, value, index) => {
    const label = labels[index];

    if (typeof value !== "number" || !label) {
      return rows;
    }

    const { labelKey, params } = label;

    return [...rows, { labelKey: `${LABEL_PREFIX}${labelKey}` as const, params, pnl: value }];
  }, []);
};

/**
 * The market's two sides, which the labels name and the value picker needs to know apart.
 *
 * A double-line market lists one runner per selection *per line*, so this reduces that to the two
 * distinct selections. The home side is whichever the exchange reports as the key line's home runner;
 * a Totals market reports none, and there the first runner stands in — the same fallback EMS makes by
 * reading `runnersInfo` straight off the sorted runner list.
 */
/** The market fields that decide which runner the bet engine treats as home. */
export type HomeRunnerMarket = {
  marketType?: string;
  keyLineHome?: { selectionId: number };
  runners: Array<{ selectionId: number }>;
};

/**
 * The runner the bet engine treats as home. On Totals the engine reads home as the Under side (it wins
 * below the line), and only the runner order says which runner that is: the exchange lists Under first,
 * which is what the Exchange Mobile Site relies on. The key line does not name a side there, so it is not
 * used. Handicap markets take the key line's home runner, falling back to the first runner.
 */
export const getHomeSelectionId = ({ marketType, keyLineHome, runners }: HomeRunnerMarket): number | undefined =>
  isHandicapMarket(marketType) ? keyLineHome?.selectionId ?? runners[0]?.selectionId : runners[0]?.selectionId;

export const getOutcomeRunners = (
  runners: Array<{ selectionId: number; name: string }>,
  homeSelectionId?: number,
): OutcomeRunners | undefined => {
  const home = runners.find(({ selectionId }) => selectionId === homeSelectionId) ?? runners[0];

  if (!home) {
    return undefined;
  }

  const away = runners.find(({ selectionId }) => selectionId !== home.selectionId);

  if (!away) {
    return undefined;
  }

  return {
    home: { selectionId: home.selectionId, name: home.name },
    away: { selectionId: away.selectionId, name: away.name },
  };
};
